import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Info, Loader2, Send, Sparkles } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Textarea } from "@/shared/ui/textarea";
import { grouped } from "@/entities/scene-card/model/creditRates";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { useToast } from "@/shared/hooks/use-toast";
import { posterService } from "../../../api/posterService";

/**
 * V26.4 (2026-05-14) — AI 改图 tab(i2i / image_edit)。
 *
 * 跟 AI 重画(t2i)的区别:
 * - i2i 保留原画面构图,只改 instruction 指定的局部
 * - 后端走 `mode: "edit"`, 透传 initImageUrl 给 ai-service → provider.image_edit
 * - 必须有 baseImageUrl(老任务 / AI_RENDER 模式可能没有, 此时强制降到 t2i 路径)
 *
 * 简化版 UI:不放 preset chip(i2i 主要靠用户描述 "改哪里"),只有 textarea
 * + 改并保留 / 改并替换。Preset 复用需要 AI 重画那种"风格/色彩/场景"分类,
 * 跟 i2i 的"局部修改"语义不太契合。
 */
export interface I2IEditTabProps {
  busy: boolean;
  hasBaseImage: boolean;
  onSubmit: (body: { instruction: string; replace: boolean }) => void;
  /**
   * 只有「替换」这一档(2026-08-08,长图分段)。
   *
   * ★长图分段的 seq 是**块序 index+2** 的硬契约 —— 没有第二个槽位放旧版。
   *   照旧渲「改并保留」等于**界面在说谎**:用户以为原图还在,实际已经被盖掉。
   *   宁可少一个按钮,也不给一个做不到的承诺。
   */
  replaceOnly?: boolean;
  /**
   * 本次要扣的积分(一张出图的钱;修复波 0926)。按钮点下去就真出一张、真扣一笔,
   * 不写数 = 让用户盲扣。价目表没到货时为 null/缺省 —— 按钮上**不出数字**,不出一个可能过期的数
   * (与同页 GroupRedrawPanel 同一写法)。
   */
  credits?: number | null;
}

export function I2IEditTab({
  busy,
  hasBaseImage,
  onSubmit,
  replaceOnly,
  credits,
}: I2IEditTabProps) {
  const costSuffix = credits == null ? "" : ` · ${grouped(credits)} 积分`;
  const { t } = useTranslation("poster");
  const confirm = useConfirm();
  const { toast } = useToast();
  const [text, setText] = useState("");
  const [optimizing, setOptimizing] = useState(false);
  const empty = !text.trim();

  // #15 改图指令优化:把口语描述交给 AI 改写成更精确的改图指令,回填到 textarea 供用户二次编辑。
  const handleOptimize = async () => {
    const raw = text.trim();
    if (!raw || optimizing || busy) return;
    setOptimizing(true);
    try {
      const optimized = await posterService.optimizeEditInstruction(raw);
      if (optimized.trim()) setText(optimized.trim());
    } catch {
      toast({ description: t("editor.i2iOptimizeFail"), variant: "destructive" });
    } finally {
      setOptimizing(false);
    }
  };

  if (!hasBaseImage) {
    return (
      <div className="space-y-3 pt-2">
        <div className="flex items-start gap-2 rounded bg-warning/10 p-3 text-xs text-warning">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>{t("editor.i2iWarnNoBase")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-2">
      <div className="flex items-start gap-2 rounded bg-info/10 p-2 text-xs text-info">
        <Info className="mt-0.5 h-3 w-3 shrink-0" />
        <span>{t("editor.i2iInfo")}</span>
      </div>

      <section className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <label className="text-2xs text-muted-foreground" htmlFor="i2i-text">
            {t("editor.i2iDescLabel")}
          </label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 gap-1 px-2 text-2xs text-primary hover:text-primary"
            disabled={busy || optimizing || empty}
            title={t("editor.i2iOptimizeHint")}
            onClick={handleOptimize}
          >
            {optimizing ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" /> {t("editor.i2iOptimizing")}
              </>
            ) : (
              <>
                <Sparkles className="h-3 w-3" /> {t("editor.i2iOptimize")}
              </>
            )}
          </Button>
        </div>
        <Textarea
          id="i2i-text"
          rows={4}
          maxLength={400}
          placeholder={t("editor.i2iPlaceholder")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={busy || optimizing}
        />
      </section>

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          variant="default"
          disabled={busy || empty}
          onClick={async () => {
            if (replaceOnly) {
              // 单槽位:改了就覆盖,没有退路 —— 所以这里必须先问一句。
              const ok = await confirm({
                title: "改完会覆盖这一块",
                description: "长图的每一块只存一版,改完拿不回上一版。确定要改吗?",
                confirmText: "改这一块",
                variant: "destructive",
              });
              if (!ok) return;
            }
            onSubmit({ instruction: text.trim(), replace: replaceOnly === true });
          }}
          className="flex-1"
        >
          {busy ? (
            <>
              <Loader2 className="mr-1 h-3 w-3 animate-spin" /> {t("editor.i2iRunning")}
            </>
          ) : (
            <>
              <Send className="mr-1 h-3 w-3" /> {replaceOnly ? "改这一块" : t("editor.i2iKeep")}
              {costSuffix}
            </>
          )}
        </Button>
        {!replaceOnly && (
          <Button
            variant="outline"
            disabled={busy || empty}
            onClick={async () => {
              const ok = await confirm({
                title: t("editor.i2iReplaceConfirmTitle"),
                description: t("editor.i2iReplaceConfirmDesc"),
                confirmText: t("editor.i2iReplaceBtn"),
                variant: "destructive",
              });
              if (ok) onSubmit({ instruction: text.trim(), replace: true });
            }}
            className="flex-1 hover:border-destructive hover:text-destructive"
          >
            {t("editor.i2iReplaceLabel")}
            {costSuffix}
          </Button>
        )}
      </div>
    </div>
  );
}
